<script setup lang="ts">
import ModalBase from "@/components/modal/modal-container.vue";
import ModalTitle from "@/components/modal/modal-title.vue";
import ModalContent from "@/components/modal/modal-content.vue";
import { faArrowRight, faExclamationTriangle } from "@fortawesome/pro-regular-svg-icons";
import ModalFooterConfirmation from "@/components/modal/modal-footer-confirmation.vue";
import useNotifications from "@/composables/notifications";
import { computed } from "vue";
import { InvoiceStatus } from "@/api/billing/constants";
import { useCancelCreditNote } from "@/composables/billing/credit-notes";

const props = defineProps<{
  docId: number
  projectCode: number
  projectName: string
  status: InvoiceStatus
}>()
const emit = defineEmits<{ close: [] }>()

const { mutateAsync, isPending, isError } = useCancelCreditNote()
const { addError, addSuccess } = useNotifications()
const creditNoteDisplayName = computed(
  () => `${props.projectName} (${props.projectCode})`
)

async function cancelCreditNote() {
  try {
    console.log('props.docId', props.docId);
    await mutateAsync(props.docId)

    addSuccess({
      title: 'Credit Note Cancelled Successfully',
      allowHtml: true,
      message: `Credit Note <b>${props.docId}</b> for <b>${creditNoteDisplayName.value}</b> has been successfully cancelled.`
    })

    emit('close')
  } catch (e: unknown) {
    addError({
      title: 'Credit Note Not Cancelled',
      allowHtml: true,
      message: `Credit Note <b>${props.docId}</b> for <b>${creditNoteDisplayName.value}</b> could not be cancelled due to an error that occurred. Please try again.`
    })
  }
}
</script>

<template>
  <ModalBase @close="$emit('close')">
    <ModalTitle>
      Cancel Credit Note Confirmation
    </ModalTitle>
    <ModalContent :icon="faExclamationTriangle" icon-variant="warning">
      <template v-if="isError">
        <p>The request to cancel Credit Note <b>{{ docId }}</b> for <b>{{ creditNoteDisplayName }}</b> was unsuccessful.</p>
        <p>Would you like to try again?</p>
      </template>
      <template v-else>
        <p>
          Are you sure you would like to cancel Credit Note <b>{{ docId }}</b> for <b>{{ creditNoteDisplayName }}</b>?
        </p>
        <p v-if="status === InvoiceStatus.Sent">
          This credit note was already sent. If you choose to proceed, it will be voided.
        </p>
      </template>
    </ModalContent>
    <ModalFooterConfirmation
      @reject="$emit('close')"
      @confirm="cancelCreditNote()"
      :pending="isPending"
      :confirm-icon="status === InvoiceStatus.Sent ? faArrowRight : undefined"
      :confirm-text="status === InvoiceStatus.Sent ? 'Proceed' : undefined"
    />
  </ModalBase>
</template>
